import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

function EditSilkscreenModal({ screen, onClose, onSuccess, customers, onAddCustomer }) {
  const [formData, setFormData] = useState({
    screenCode: '',
    currentCustomerName: '',
    currentItemDescription: '',
    amountAcross: '',
    amountAround: ''
  });
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [artworkFile, setArtworkFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (screen) {
      setFormData({
        screenCode: screen.screenCode || '',
        currentCustomerName: screen.currentCustomerName || '',
        currentItemDescription: screen.currentItemDescription || '',
        amountAcross: screen.amountAcross || '',
        amountAround: screen.amountAround || ''
      });

      // Find matching customer
      const customer = customers.find(c => c.name === screen.currentCustomerName);
      if (customer) {
        setSelectedCustomerId(customer.id.toString());
      }

      // Set preview if artwork exists
      if (screen.artworkPreviewPath) {
        setPreviewUrl(`${API_URL}${screen.artworkPreviewPath}`);
      }
    }
  }, [screen, customers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCustomerChange = (e) => {
    const value = e.target.value;
    if (value === 'ADD_NEW') {
      onAddCustomer();
      return;
    }
    setSelectedCustomerId(value);
    const customer = customers.find(c => c.id === value);
    if (customer) {
      setFormData(prev => ({ ...prev, currentCustomerName: customer.name }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 4 * 1024 * 1024) {
        setError('File size must be less than 4MB');
        return;
      }
      if (file.type !== 'application/pdf') {
        setError('Only PDF files are allowed');
        return;
      }
      setArtworkFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.screenCode || !selectedCustomerId || !formData.currentItemDescription || 
        !formData.amountAcross || !formData.amountAround) {
      setError('All fields are required');
      return;
    }

    if (parseInt(formData.amountAcross) <= 0 || parseInt(formData.amountAround) <= 0) {
      setError('Amount across and around must be greater than 0');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('screenCode', formData.screenCode);
      formDataToSend.append('currentCustomerName', formData.currentCustomerName);
      formDataToSend.append('currentItemDescription', formData.currentItemDescription);
      formDataToSend.append('amountAcross', formData.amountAcross);
      formDataToSend.append('amountAround', formData.amountAround);
      
      if (artworkFile) {
        formDataToSend.append('artworkPdf', artworkFile);
      }

      const response = await fetch(`${API_URL}/silkscreens/${screen.id}`, {
        method: 'PUT',
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update silkscreen');
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: 24, color: '#1976d2' }}>Edit Silkscreen</h2>
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#333' }}>
              Screen Code *
            </label>
            <input
              type="text"
              name="screenCode"
              value={formData.screenCode}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
              required
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#333' }}>
              Customer *
            </label>
            <select
              value={selectedCustomerId}
              onChange={handleCustomerChange}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
              required
            >
              <option value="">Select Customer</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
              <option value="ADD_NEW">+ Add New Customer</option>
            </select>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#333' }}>
              Item Description *
            </label>
            <input
              type="text"
              name="currentItemDescription"
              value={formData.currentItemDescription}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#333' }}>
                Amount Across *
              </label>
              <input
                type="number"
                name="amountAcross"
                value={formData.amountAcross}
                onChange={handleChange}
                min="1"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#333' }}>
                Amount Around *
              </label>
              <input
                type="number"
                name="amountAround"
                value={formData.amountAround}
                onChange={handleChange}
                min="1"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#333' }}>
              Artwork PDF {screen.currentArtworkPdfPath ? '(Optional - leave empty to keep current)' : '(Optional)'}
            </label>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            {previewUrl && (
              <div style={{ marginTop: 12 }}>
                <img 
                  src={previewUrl} 
                  alt="Artwork Preview" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '200px',
                    border: '1px solid #ddd',
                    borderRadius: '6px'
                  }} 
                />
              </div>
            )}
          </div>

          {error && (
            <div style={{
              padding: '12px',
              background: '#ffebee',
              color: '#c62828',
              borderRadius: '6px',
              marginBottom: 20,
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 24px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                background: 'white',
                color: '#666',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 24px',
                border: 'none',
                borderRadius: '6px',
                background: loading ? '#ccc' : '#1976d2',
                color: 'white',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              {loading ? 'Updating...' : 'Update Silkscreen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditSilkscreenModal;
